import { useMemo, useState } from 'react';
import { Copy, Check, MessageCircle, Mail, Link as LinkIcon } from 'lucide-react';
import { Button } from '@/components/ui';
import { useNotification } from '@/hooks/useNotification';

interface Props {
  candidateName: string | null | undefined;
  candidatePhone?: string | null;
  candidateEmail?: string | null;
  inviteToken: string | null | undefined;
}

const buildInviteUrl = (token: string): string => {
  if (typeof window === 'undefined') return `/onboarding/invite/${token}`;
  return `${window.location.origin}/onboarding/invite/${encodeURIComponent(token)}`;
};

const buildMessage = (candidateName: string | null | undefined, url: string): string => {
  const greeting = candidateName ? `Hi ${candidateName},` : 'Hi,';
  return `${greeting}\n\nWelcome to Roundglass Foundation. Please use the link below to complete your onboarding (the link is valid for 24 hours):\n\n${url}\n\nFor any help, message us on WhatsApp at +91 7347012411.`;
};

const buildWhatsAppShareUrl = (
  url: string,
  candidateName: string | null | undefined,
  phone?: string | null,
): string => {
  const text = encodeURIComponent(buildMessage(candidateName, url));
  // If we have the candidate's phone, address the message to them directly.
  // Otherwise open the share sheet so the operator picks the recipient.
  if (phone) {
    const cleaned = phone.replace(/\D/g, '');
    const intl = cleaned.startsWith('91') ? cleaned : `91${cleaned}`;
    return `https://wa.me/${intl}?text=${text}`;
  }
  return `https://wa.me/?text=${text}`;
};

const buildMailtoUrl = (
  url: string,
  candidateName: string | null | undefined,
  email?: string | null,
): string => {
  const subject = encodeURIComponent('Your Roundglass Foundation onboarding link');
  const body = encodeURIComponent(buildMessage(candidateName, url));
  const to = email ? encodeURIComponent(email) : '';
  return `mailto:${to}?subject=${subject}&body=${body}`;
};

export const MagicLinkPanel = ({
  candidateName,
  candidatePhone,
  candidateEmail,
  inviteToken,
}: Props) => {
  const { showSuccess, showError } = useNotification();
  const [copied, setCopied] = useState(false);

  const inviteUrl = useMemo(
    () => (inviteToken ? buildInviteUrl(inviteToken) : ''),
    [inviteToken],
  );

  const handleCopy = async () => {
    if (!inviteUrl) return;
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      showSuccess('Link Copied', 'The onboarding link is on your clipboard.');
      window.setTimeout(() => setCopied(false), 2500);
    } catch {
      showError('Copy Failed', 'Could not copy the link. Please copy it manually.');
    }
  };

  return (
    <div className="card">
      <div className="card-header">
        <h3 className="text-base font-semibold text-rgf-gray-900">Candidate Magic Link</h3>
      </div>
      <div className="card-body space-y-3">
        {!inviteToken ? (
          <p className="text-sm text-rgf-gray-600">
            A magic link will be available once the case is in an eligible onboarding state.
          </p>
        ) : (
          <>
            <p className="text-xs text-rgf-gray-500">
              Share this 24-hour link with the candidate via WhatsApp or email so they can submit
              their details, upload documents, and track approvals.
            </p>
            <div className="flex items-center gap-2 rounded-lg border border-rgf-gray-200 bg-rgf-gray-50 px-3 py-2">
              <LinkIcon className="h-4 w-4 text-rgf-gray-400 flex-shrink-0" />
              <code className="text-xs text-rgf-gray-700 flex-1 truncate" title={inviteUrl}>
                {inviteUrl}
              </code>
              <Button
                variant="ghost"
                size="sm"
                icon={copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                onClick={handleCopy}
                title="Copy link"
              >
                {copied ? 'Copied' : 'Copy'}
              </Button>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <a
                href={buildWhatsAppShareUrl(inviteUrl, candidateName, candidatePhone)}
                target="_blank"
                rel="noreferrer"
              >
                <Button variant="secondary" size="sm" icon={<MessageCircle className="h-4 w-4" />}>
                  Share on WhatsApp
                </Button>
              </a>
              <a
                href={buildMailtoUrl(inviteUrl, candidateName, candidateEmail)}
                target="_blank"
                rel="noreferrer"
              >
                <Button variant="secondary" size="sm" icon={<Mail className="h-4 w-4" />}>
                  Share via Email
                </Button>
              </a>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
